import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { activateFallbackAccount } from '../api/fallback-auth';
import { usePlatformRuntime } from '../runtime';
import { useAuth } from '../contexts/AuthContext';

type PageStatus = 'loading' | 'ready' | 'submitting' | 'success' | 'error';

interface ActivationError {
  title: string;
  message: string;
}

const MIN_PASSWORD_LENGTH = 8;

function classifyError(error: unknown): ActivationError {
  const isAxios = axios.isAxiosError(error);
  const responseMessage = isAxios ? (error.response?.data?.message ?? '') : '';
  const statusCode = isAxios ? error.response?.status : undefined;
  const lowerMessage = responseMessage.toLowerCase();

  if (statusCode === 410 || lowerMessage.includes('expired')) {
    return {
      title: 'Link Expired',
      message:
        'This activation link has expired. Please contact your administrator for a new invitation.',
    };
  }

  if (lowerMessage.includes('revoked')) {
    return {
      title: 'Invitation Revoked',
      message: 'This invitation has been revoked. Please contact your administrator.',
    };
  }

  if (
    lowerMessage.includes('already activated') ||
    lowerMessage.includes('already used') ||
    lowerMessage.includes('already reused')
  ) {
    return {
      title: 'Already Activated',
      message: 'This account has already been activated. Please sign in instead.',
    };
  }

  if (statusCode === 423 || lowerMessage.includes('locked')) {
    return {
      title: 'Account Locked',
      message:
        'This account has been locked due to too many failed attempts. Please contact your administrator.',
    };
  }

  if (statusCode === 403 || lowerMessage.includes('disabled')) {
    return {
      title: 'Activation Unavailable',
      message: 'Fallback activation is not available. Please contact your administrator.',
    };
  }

  if (statusCode === 400 || lowerMessage.includes('invalid')) {
    return {
      title: 'Invalid Link',
      message:
        'This activation link is invalid. Please check the link or contact your administrator.',
    };
  }

  return {
    title: 'Activation Failed',
    message:
      responseMessage ||
      'An unexpected error occurred. Please try again or contact your administrator.',
  };
}

export function ActivatePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const runtime = usePlatformRuntime();
  const { user, refreshUser } = useAuth();
  const initialized = useRef(false);

  const [status, setStatus] = useState<PageStatus>('loading');
  const [activationError, setActivationError] = useState<ActivationError | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');

  const token = searchParams.get('token');
  const code = searchParams.get('code');

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    if (!token || !code) {
      setStatus('error');
      setActivationError({
        title: 'Invalid Link',
        message:
          'This activation link is missing required parameters. Please check the link or contact your administrator.',
      });
      return;
    }

    setStatus('ready');
  }, [token, code]);

  const validateForm = (): boolean => {
    if (password.length < MIN_PASSWORD_LENGTH) {
      setValidationError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return false;
    }

    if (password !== confirmPassword) {
      setValidationError('Passwords do not match.');
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm() || !token || !code) return;

    setStatus('submitting');
    setValidationError('');

    try {
      const tokenResponse = await activateFallbackAccount({ token, code, password });
      runtime.session.setAuthToken(tokenResponse.accessToken);
      const refreshedUser = await refreshUser();
      if (refreshedUser) {
        setStatus('success');
      } else {
        setStatus('error');
        setActivationError({
          title: 'Sign-In Failed',
          message:
            'Your account was activated but we could not sign you in automatically. Please sign in manually.',
        });
      }
    } catch (error) {
      setStatus('error');
      setActivationError(classifyError(error));
    }
  };

  useEffect(() => {
    if (status === 'success' && user) {
      navigate('/products', { replace: true });
    }
  }, [status, user, navigate]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Loading...</h2>
        </div>
      </div>
    );
  }

  if (status === 'error' && activationError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">{activationError.title}</h2>
          <p className="text-gray-600 mt-2 mb-6" role="alert">
            {activationError.message}
          </p>
          <Link
            to="/"
            className="inline-block bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-600/90"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Account Activated</h2>
          <p className="text-gray-600 mt-2">Redirecting you to the application...</p>
          <Link
            to="/products"
            className="inline-block mt-4 text-sm text-primary-600 hover:text-primary-500"
          >
            Click here if not redirected
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg
              className="h-6 w-6 text-primary-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Activate Your Account</h2>
          <p className="text-gray-600 mt-2">Create a password to complete your account setup.</p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setValidationError('');
              }}
              required
              minLength={MIN_PASSWORD_LENGTH}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter your password"
            />
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(event) => {
                setConfirmPassword(event.target.value);
                setValidationError('');
              }}
              required
              minLength={MIN_PASSWORD_LENGTH}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Confirm your password"
            />
          </div>

          {validationError ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md" role="alert">
              <p className="text-sm text-red-600">{validationError}</p>
            </div>
          ) : null}

          <p className="text-xs text-gray-500">
            Password must be at least {MIN_PASSWORD_LENGTH} characters.
          </p>

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-md hover:bg-primary-600/90 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === 'submitting' ? 'Activating...' : 'Activate Account'}
          </button>
        </form>

        <p className="text-sm text-gray-500 mt-6 text-center">
          <span>Already have an account? </span>
          <Link to="/" className="text-primary-600 hover:text-primary-500">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
